// assets/infinite-scroll.js

class InfiniteScrollDawn extends HTMLElement {
  constructor() {
    super();

    // Avoid double-init if Shopify re-renders sections
    if (this.initialized) return;
    this.initialized = true;

    this.grid = document.querySelector('#product-grid');
    if (!this.grid) {
      this.style.display = 'none';
      return;
    }

    this.nextUrl = this.dataset.nextUrl || '';
    this.endText = this.dataset.endText || 'You Have Reached The End';
    this.loadMoreText = this.dataset.loadMoreText || 'Load More';
    this.loadingText = this.dataset.loadingText || 'Loading More';

    this.isLoading = false;
    this.autoMode = false; // we only enable scroll auto-load after first click

    this.render();

    if (!this.nextUrl) {
      // Only one page – show end state
      this.setFinished();
      return;
    }

    // FIRST CLICK: manual load and then enable auto scroll
    this.button.addEventListener('click', () => {
      if (!this.autoMode) {
        this.autoMode = true;
        this.loadNextPage();
        this.setupObserver(); // turn on auto-load after first interaction
      } else {
        this.loadNextPage();
      }
    });
  }

  setupObserver() {
    // Don't create twice or if there's no next page
    if (this.observer || !this.nextUrl) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isLoading && this.nextUrl) {
            this.loadNextPage();
          }
        });
      },
      {
        root: null,
        rootMargin: '200px 0px 200px 0px',
        threshold: 0
      }
    );

    this.observer.observe(this);
  }

  render() {
    this.innerHTML = `
      <div class="infinite-scroll__inner">
        <button type="button" class="infinite-scroll__button">
          ${this.loadMoreText}
        </button>
        <p class="infinite-scroll__status" aria-live="polite"></p>
      </div>
    `;
    this.button = this.querySelector('.infinite-scroll__button');
    this.status = this.querySelector('.infinite-scroll__status');
  }

  setFinished() {
    if (!this.button) return;
    this.button.disabled = true;
    this.button.classList.add('infinite-scroll__button--finished');
    this.button.textContent = this.endText;
  }

  setIdle() {
    if (!this.button) return;
    this.button.disabled = false;
    this.button.classList.remove('infinite-scroll__button--finished');
    this.button.textContent = this.loadMoreText;
  }

  setLoading() {
    if (!this.button) return;
    this.button.disabled = true;
    this.button.textContent = this.loadingText;
  }

  loadNextPage() {
    if (!this.nextUrl || this.isLoading) return;

    this.isLoading = true;
    this.setLoading();

    fetch(this.nextUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) throw new Error('Network error');
        return response.text();
      })
      .then((htmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');

        const newGrid = doc.querySelector('#product-grid');
        if (!newGrid) throw new Error('Next page product grid not found');

        const newItems = Array.from(newGrid.children);
        newItems.forEach((item) => {
          item.removeAttribute('id'); // safety
          this.grid.appendChild(item);
        });

        const newInfinite = doc.querySelector('infinite-scroll');
        if (newInfinite && newInfinite.dataset.nextUrl) {
          this.nextUrl = newInfinite.dataset.nextUrl;
        } else {
          this.nextUrl = '';
        }

        if (!this.nextUrl) {
          this.setFinished();
          if (this.observer) this.observer.disconnect();
        } else {
          this.setIdle();
        }
      })
      .catch((error) => {
        console.error('Infinite scroll error:', error);
        if (this.status) {
          this.status.textContent = 'Something went wrong loading more products.';
        }
        this.setIdle();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}

// Register the custom element once
if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScrollDawn);
}
